import L from 'leaflet';

/**
 * Reshaping a route, always available to the driver of that car.
 *
 * There is no mode to turn on: if the road shown is yours, you can pull it. The
 * problem that usually forces a mode is gesture conflict, since dragging the map
 * and dragging the line are the same gesture. It is solved here by giving each
 * pointer type the interaction it can do unambiguously:
 *
 *  - Handles sitting on the route are draggable markers. Leaflet already knows a
 *    drag that starts on a marker is not a map pan, on mouse and on touch alike,
 *    so this path never fights the map and is the whole story on a phone.
 *  - With a mouse, the line itself can also be grabbed anywhere, the way Google
 *    Maps does it. That is only enabled for a fine pointer, where the cursor
 *    shows what is grabbable before anything moves and a stray pixel of movement
 *    does not cost a mis-drag.
 *
 * Nothing is routed while dragging. The line follows as a straight rubber band
 * and the real road is fetched once, on release.
 */

const GRAB_RADIUS_PX = 18;
/** Below this, a leg is too short on screen to carry a grabbable handle. */
const MIN_LEG_PX = 48;

function nearestIndex(map, points, containerPoint) {
  let bestIdx = -1;
  let bestDist = Infinity;
  const step = points.length > 900 ? 4 : 1;
  for (let i = 0; i < points.length; i += step) {
    const p = map.latLngToContainerPoint(points[i]);
    const d = (p.x - containerPoint.x) ** 2 + (p.y - containerPoint.y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  if (step > 1) {
    for (let i = Math.max(0, bestIdx - step); i < Math.min(points.length, bestIdx + step); i += 1) {
      const p = map.latLngToContainerPoint(points[i]);
      const d = (p.x - containerPoint.x) ** 2 + (p.y - containerPoint.y) ** 2;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
  }
  return { index: bestIdx, distance: Math.sqrt(bestDist) };
}

function legForIndex(waypointIndexes, index) {
  for (let j = waypointIndexes.length - 2; j >= 0; j -= 1) {
    if (index >= waypointIndexes[j]) return j;
  }
  return 0;
}

/** Rebuild the stored shape: each point anchored to the stop it follows, in order. */
function rebuildVia(sequence, positions) {
  const out = [];
  let anchor = 'origin';
  for (const w of sequence) {
    if (w.kind === 'stop') anchor = w.id;
    else if (w.kind === 'via') {
      const at = positions.get(w.id);
      if (at) out.push({ id: w.id, after: anchor, lat: at.lat, lng: at.lng });
    }
  }
  return out;
}

const newViaId = () => `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const handleIcon = (kind) =>
  L.divIcon({
    className: 'leaflet-div-icon-plain',
    html: `<span class="route-handle route-handle-${kind}"></span>`,
    iconSize: kind === 'add' ? [14, 14] : [18, 18],
    iconAnchor: kind === 'add' ? [7, 7] : [9, 9],
  });

export function attachRouteEditor(map, editor) {
  const { points, waypoints, waypointIndexes, via, color, onCommit } = editor;
  if (!points?.length || !waypoints?.length || waypointIndexes?.length !== waypoints.length) {
    return () => {};
  }

  const container = map.getContainer();
  const finePointer = window.matchMedia?.('(pointer: fine)')?.matches ?? false;
  const layers = [];
  const add = (layer) => {
    layer.addTo(map);
    layers.push(layer);
    return layer;
  };
  const drop = (layer) => {
    layer.remove();
    const i = layers.indexOf(layer);
    if (i !== -1) layers.splice(i, 1);
  };

  const positions = new Map(via.map((v) => [v.id, { lat: v.lat, lng: v.lng }]));

  /** Straight preview between the two fixed ends of the leg being pulled. */
  let rubber = null;
  const showRubber = (from, at, to) => {
    if (!rubber) {
      rubber = add(
        L.polyline([from, at, to], { color, weight: 3, opacity: 0.95, dashArray: '6 7', interactive: false }),
      );
    } else {
      rubber.setLatLngs([from, at, to]);
    }
  };
  const hideRubber = () => {
    if (rubber) {
      drop(rubber);
      rubber = null;
    }
  };

  /* ------------------------------------------------- handles on the route -- */

  // Existing shaping points: drag to move, tap to remove.
  for (const point of via) {
    const marker = add(
      L.marker([point.lat, point.lng], {
        icon: handleIcon('via'),
        draggable: true,
        zIndexOffset: 1300,
        keyboard: false,
      }),
    );
    marker.on('drag', () => {
      const at = marker.getLatLng();
      positions.set(point.id, { lat: at.lat, lng: at.lng });
    });
    marker.on('dragend', () => onCommit(rebuildVia(waypoints, positions)));
    marker.on('click', (e) => {
      L.DomEvent.stop(e);
      positions.delete(point.id);
      onCommit(rebuildVia(waypoints.filter((w) => w.id !== point.id), positions));
    });
    marker.bindTooltip('Glissez pour deplacer · touchez pour retirer', { direction: 'top', offset: [0, -12] });
  }

  /**
   * One grab handle per leg, so the route is pullable without hunting for it.
   * This is the only path that works on touch, so it is always drawn.
   *
   * Rebuilt whenever the view settles: whether a leg is long enough to carry a
   * handle is a question about pixels, and the answer changes with every zoom.
   * Computing it once at attach time left routes handle-less after a fit.
   */
  let pullHandles = [];
  let dragging = false;

  const renderPullHandles = () => {
    if (dragging) return; // never pull the ground out from under a live drag
    for (const m of pullHandles) drop(m);
    pullHandles = [];

    for (let leg = 0; leg < waypointIndexes.length - 1; leg += 1) {
      const from = points[waypointIndexes[leg]];
      const to = points[waypointIndexes[leg + 1]];
      const a = map.latLngToContainerPoint(from);
      const b = map.latLngToContainerPoint(to);
      if (Math.hypot(b.x - a.x, b.y - a.y) < MIN_LEG_PX) continue;

      const midIndex = Math.round((waypointIndexes[leg] + waypointIndexes[leg + 1]) / 2);
      const marker = add(
        L.marker(points[midIndex], {
          icon: handleIcon('add'),
          draggable: true,
          zIndexOffset: 1200,
          keyboard: false,
        }),
      );
      pullHandles.push(marker);

      const id = newViaId();
      marker.on('dragstart', () => {
        dragging = true;
        showRubber(from, marker.getLatLng(), to);
      });
      marker.on('drag', () => showRubber(from, marker.getLatLng(), to));
      marker.on('dragend', () => {
        dragging = false;
        hideRubber();
        const at = marker.getLatLng();
        positions.set(id, { lat: at.lat, lng: at.lng });
        const sequence = [...waypoints];
        sequence.splice(leg + 1, 0, { kind: 'via', id });
        onCommit(rebuildVia(sequence, positions));
      });
      marker.bindTooltip('Glissez pour passer par une autre route', { direction: 'top', offset: [0, -10] });
    }
  };

  renderPullHandles();
  map.on('zoomend moveend', renderPullHandles);

  /* ------------------------------------ grabbing the line itself (mouse) -- */

  const teardownHandles = () => {
    map.off('zoomend moveend', renderPullHandles);
    hideRubber();
    for (const layer of [...layers]) layer.remove();
    layers.length = 0;
  };

  if (!finePointer) return teardownHandles;

  let ghost = null;
  let drag = null;

  const showGhost = (latlng) => {
    if (!ghost) ghost = add(L.marker(latlng, { icon: handleIcon('ghost'), interactive: false, zIndexOffset: 1100 }));
    else ghost.setLatLng(latlng);
  };
  const hideGhost = () => {
    if (ghost) {
      drop(ghost);
      ghost = null;
    }
  };

  const pointAt = (event) => {
    const rect = container.getBoundingClientRect();
    return L.point(event.clientX - rect.left, event.clientY - rect.top);
  };

  const onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('.leaflet-marker-icon, .leaflet-control')) return;
    const cp = pointAt(event);
    const { index, distance } = nearestIndex(map, points, cp);
    if (distance > GRAB_RADIUS_PX) return; // not on the line: let the map pan

    event.preventDefault();
    container.setPointerCapture?.(event.pointerId);
    map.dragging.disable();

    const leg = legForIndex(waypointIndexes, index);
    const latlng = map.containerPointToLatLng(cp);
    drag = { pointerId: event.pointerId, leg, from: points[waypointIndexes[leg]], to: points[waypointIndexes[leg + 1]] };
    showRubber(drag.from, latlng, drag.to);
    showGhost(latlng);
    container.classList.add('route-dragging');
  };

  const onPointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    const cp = pointAt(event);
    if (!drag) {
      if (event.target.closest('.leaflet-marker-icon')) {
        hideGhost();
        container.classList.remove('route-grabbable');
        return;
      }
      const { index, distance } = nearestIndex(map, points, cp);
      if (distance <= GRAB_RADIUS_PX) {
        showGhost(points[index]);
        container.classList.add('route-grabbable');
      } else {
        hideGhost();
        container.classList.remove('route-grabbable');
      }
      return;
    }
    if (event.pointerId !== drag.pointerId) return;
    const latlng = map.containerPointToLatLng(cp);
    showRubber(drag.from, latlng, drag.to);
    showGhost(latlng);
  };

  const endDrag = (event, commit) => {
    if (!drag || (event && event.pointerId !== drag.pointerId)) return;
    const current = drag;
    drag = null;
    hideRubber();
    container.classList.remove('route-dragging');
    map.dragging.enable();

    const latlng = ghost?.getLatLng();
    hideGhost();
    if (!commit || !latlng) return;

    const id = newViaId();
    positions.set(id, { lat: latlng.lat, lng: latlng.lng });
    const sequence = [...waypoints];
    sequence.splice(current.leg + 1, 0, { kind: 'via', id });
    onCommit(rebuildVia(sequence, positions));
  };

  const onPointerUp = (e) => endDrag(e, true);
  const onPointerCancel = (e) => endDrag(e, false);
  const onLeave = () => {
    if (!drag) {
      hideGhost();
      container.classList.remove('route-grabbable');
    }
  };

  container.addEventListener('pointerdown', onPointerDown);
  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointerup', onPointerUp);
  container.addEventListener('pointercancel', onPointerCancel);
  container.addEventListener('pointerleave', onLeave);

  return () => {
    container.removeEventListener('pointerdown', onPointerDown);
    container.removeEventListener('pointermove', onPointerMove);
    container.removeEventListener('pointerup', onPointerUp);
    container.removeEventListener('pointercancel', onPointerCancel);
    container.removeEventListener('pointerleave', onLeave);
    container.classList.remove('route-grabbable', 'route-dragging');
    map.dragging.enable();
    teardownHandles();
  };
}
